import { FC, useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { WaterfallViewModel } from '../../models/waterfall';

const NAME_PATTERN = /^[A-Za-z][A-Za-z0-9]*$/;

interface CopyWaterfallDialogProps {
  visible: boolean;
  source: WaterfallViewModel | null;
  onClose: () => void;
  onConfirm: (name: string, description: string) => Promise<void> | void;
}

export const CopyWaterfallDialog: FC<CopyWaterfallDialogProps> = ({ visible, source, onClose, onConfirm }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (visible && source) {
      setName(`${source.name}Copy`.slice(0, 50));
      setDescription(source.description ?? '');
      setSubmitting(false);
    }
  }, [visible, source]);

  const nameError =
    name.trim().length === 0
      ? 'Name is required'
      : !NAME_PATTERN.test(name)
        ? 'Must start with a letter and contain only letters and numbers'
        : null;
  const descriptionError = description.trim().length === 0 ? 'Description is required' : null;
  const canSubmit = !nameError && !descriptionError && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) {
      return;
    }
    setSubmitting(true);
    try {
      await onConfirm(name.trim(), description.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onClose}
      header={source ? `Copy Waterfall — ${source.name}` : 'Copy Waterfall'}
      style={{ width: '90vw', maxWidth: '480px' }}
      modal
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" className="p-button-text" onClick={onClose} disabled={submitting} />
          <Button type="button" label="Copy" icon="pi pi-clone" onClick={handleConfirm} disabled={!canSubmit} loading={submitting} />
        </div>
      }
    >
      <div className="p-fluid flex flex-col gap-4 pt-2">
        <div>
          <label htmlFor="copy-wf-name" className="font-medium block mb-2">
            Name <span className="text-red-500">*</span>
          </label>
          <InputText
            id="copy-wf-name"
            value={name}
            maxLength={50}
            onChange={(e) => setName(e.target.value)}
            className={`w-full ${nameError ? 'p-invalid' : ''}`}
          />
          {nameError && <small className="p-error block mt-1">{nameError}</small>}
        </div>
        <div>
          <label htmlFor="copy-wf-description" className="font-medium block mb-2">
            Description <span className="text-red-500">*</span>
          </label>
          <InputTextarea
            id="copy-wf-description"
            value={description}
            maxLength={100}
            rows={3}
            onChange={(e) => setDescription(e.target.value)}
            className={`w-full ${descriptionError ? 'p-invalid' : ''}`}
          />
          {descriptionError && <small className="p-error block mt-1">{descriptionError}</small>}
        </div>
        <small className="text-gray-500">
          Copies the current version, including all steps and rules, into a new waterfall of the same type.
        </small>
      </div>
    </Dialog>
  );
};
